import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Grid,
  Button,
  CssBaseline,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Menu,
  MenuItem,
  Tooltip,
  useTheme,
} from "@mui/material";

import { Close, ExpandMore as ExpandMoreIcon, ListAlt } from "@mui/icons-material";
import { tokens } from "../../../theme/theme";
import { callApi } from "../../../utils/apiClient";
import { Production_API_ENDPOINTS } from "../../../config/api/apiCallProduction";

import CallAlertBar from "../../../Layout/Alertbar";
import RepulpWasteEntry from "./RepulpWasteEntry";
import HelpIcon from "@mui/icons-material/HelpOutline";
import ModuleHelpDialog from "../../../components/ModuleHelpDialog";
import { moduleHelpData } from "../../../data/moduleHelpData";
import dayjs from "dayjs";

const RepulpWasteListIndex = ({ handleBackWinderMachineEntry }) => {
  const [list, setList] = useState([]);
  const [filtered, setFiltered] = useState([]);

  const [filters, setFilters] = useState({
    id: "",
    entry_date: "",
    stage: "",
    machine_name: "",
    batch_no: "",
    reel_no: "",
    waste_type_name: "",
    quantity: "",
    uom: "",
    shift_name: "",
    shift_incharge_name: "",
    remarks: "",
    status: "",
  });

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState({});

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("info");
  const [alertMessage, setAlertMessage] = useState("");
  const [alertTitle, setAlertTitle] = useState("");

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedOption, setSelectedOption] = useState("All Entries");
  const openMenu = Boolean(anchorEl);

  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleMenuItemClick = (option) => {
    setSelectedOption(option);
    handleMenuClose();

    if (option === "All Entries") {
      setFiltered(list);
    } else if (option === "Approved Entries") {
      setFiltered(list.filter((x) => x.status === true));
    } else if (option === "Pending Entries") {
      setFiltered(list.filter((x) => x.status === false));
    }
  };

  // Help Dialog
  const [helpOpen, setHelpOpen] = useState(false);
  const handleHelpMenuClick = () => setHelpOpen(true);
  const handleHelpClose = () => setHelpOpen(false);

  // Load List
  const loadList = () => {
    const payload = {
      p_company_id: localStorage.getItem("companyid"),
      p_plant_id: 1,
    };

    callApi(
      Production_API_ENDPOINTS.repulp?.list || "/api/repulp/list",
      "POST",
      { "Content-Type": "application/json" },
      payload
    )
      .then((data) => {
        if (data && data.length > 0) {
          setList(data);
          setFiltered(data);
        } else {
          setList([]);
          setFiltered([]);
          setAlertSeverity("warning");
          setAlertTitle("Warning");
          setAlertMessage("No repulp waste entries found!");
          setAlertOpen(true);
        }
      })
      .catch(() => {
        setList([]);
        setFiltered([]);
        setAlertSeverity("error");
        setAlertTitle("Error");
        setAlertMessage("Unable to load repulp waste entries");
        setAlertOpen(true);
      });
  };

  useEffect(() => {
    loadList();
  }, []);

  // Filters
  useEffect(() => {
    const f = list.filter((row) => {
      return Object.keys(filters).every((key) => {
        if (!filters[key]) return true;
        return String(row[key] || "").toLowerCase().includes(filters[key].toLowerCase());
      });
    });
    setFiltered(f);
  }, [filters, list]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleAdd = () => {
    setFormData({});
    setIsFormOpen(true);
  };

  const handleEdit = (row) => {
    const payload = {
      p_company_id: localStorage.getItem("companyid"),
      p_plant_id: 1,
      p_entry_id: row.id,
    };

    callApi(
      Production_API_ENDPOINTS.repulp?.getById || "/api/repulp/getById",
      "POST",
      { "Content-Type": "application/json" },
      payload
    )
      .then((data) => {
        if (data && data.length > 0) {
          setFormData(data[0]);
          setIsFormOpen(true);
        } else {
          setAlertSeverity("warning");
          setAlertTitle("Warning");
          setAlertMessage("No data found for selected entry");
          setAlertOpen(true);
        }
      })
      .catch(() => {
        setAlertSeverity("error");
        setAlertTitle("Error");
        setAlertMessage("Failed to load repulp waste entry details");
        setAlertOpen(true);
      });
  };

  const handleExit = () => {
    handleBackWinderMachineEntry();
  };

  return (
    <Grid>
      {!isFormOpen && (
        <>
          <CssBaseline />

          <AppBar
            position="static"
            sx={{
              backgroundColor: "inherit",
              height: "48px",
              justifyContent: "center",
            }}
          >
            <Toolbar>
              <Button
                color="secondary"
                onClick={handleMenuClick}
                endIcon={<ExpandMoreIcon sx={{ color: "teal", fontSize: 18 }} />}
                sx={{ textTransform: "none", fontSize: "16px", fontWeight: 500 }}
              >
                {selectedOption}
              </Button>

              <Menu anchorEl={anchorEl} open={openMenu} onClose={handleMenuClose}>
                <MenuItem onClick={() => handleMenuItemClick("All Entries")}>All Entries</MenuItem>
                <MenuItem onClick={() => handleMenuItemClick("Approved Entries")}>Approved Entries</MenuItem>
                <MenuItem onClick={() => handleMenuItemClick("Pending Entries")}>Pending Entries</MenuItem>
              </Menu>

              <Button
                size="small"
                variant="contained"
                color="secondary"
                startIcon={<ListAlt />}
                sx={{ ml: "auto", textTransform: "none" }}
                onClick={handleAdd}
              >
                New Entry
              </Button>

              <Tooltip title="Help" arrow>
                <IconButton onClick={handleHelpMenuClick}>
                  <HelpIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>

              <ModuleHelpDialog
                open={helpOpen}
                onClose={handleHelpClose}
                data={moduleHelpData.WarehouseMappingMaster}
              />

              <IconButton onClick={handleExit} sx={{ ml: 1 }}>
                <Close style={{ fontSize: 16, color: "#2196F3" }} />
              </IconButton>
            </Toolbar>
          </AppBar>

          <CallAlertBar
            parentRollMsgOpen={alertOpen}
            setParentRollMsgOpen={setAlertOpen}
            parentRollSeverityvalue={alertSeverity}
            setParentRollSeverityvalue={setAlertSeverity}
            parentRollAlertTitleValue={alertTitle}
            setParentRollAlertTitleValue={setAlertTitle}
            parentRollMessageValue={alertMessage}
            setParentRollMessageValue={setAlertMessage}
          />

          <Grid container padding={1}>
            <Grid item xs={12}>
              <Box
                sx={{
                  "& .MuiTableCell-root": {
                    border: "1px solid",
                    borderColor: `${colors.primary[400]} !important`,
                    padding: "6px",
                    whiteSpace: "nowrap", // no word wrap
                  },
                  maxHeight: "75vh",
                  overflowY: "auto",
                  overflowX: "auto", // horizontal scroll
                }}
              >
                <TableContainer>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Entry Date</TableCell>
                        <TableCell>Stage</TableCell>
                        <TableCell>Machine</TableCell>
                        <TableCell>Batch No</TableCell>
                        <TableCell>Reel No</TableCell>
                        <TableCell>Waste Type</TableCell>
                        <TableCell>Quantity</TableCell>
                        <TableCell>UOM</TableCell>
                        <TableCell>Shift</TableCell>
                        <TableCell>Shift Incharge</TableCell>
                        <TableCell>Remarks</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>

                      <TableRow>
                        {Object.keys(filters).map((col) => (
                          <TableCell key={col}>
                            <TextField
                              variant="outlined"
                              size="small"
                              placeholder="Search..."
                              fullWidth
                              value={filters[col] || ""}
                              onChange={(e) => handleFilterChange(col, e.target.value)}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {filtered.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell
                            sx={{ color: "teal", textDecoration: "underline", cursor: "pointer" }}
                            onClick={() => handleEdit(row)}
                          >
                            {row.id}
                          </TableCell>
                         <TableCell>
  {row.entry_date ? dayjs(row.entry_date).format("DD-MMM-YYYY") : "-"}
</TableCell>
                          <TableCell>{row.location_name}</TableCell>
                          <TableCell>{row.machine_name}</TableCell>
                          <TableCell>{row.batch_no}</TableCell>
                          <TableCell>{row.reel_no}</TableCell>
                          <TableCell>{row.waste_type_name}</TableCell>
                          <TableCell>{row.quantity}</TableCell>
                          <TableCell>{row.uom}</TableCell>
                          <TableCell>{row.shift_name}</TableCell>
                          <TableCell>{row.shift_incharge_name}</TableCell>
                          <TableCell>{row.remarks}</TableCell>
                          <TableCell>{row.status ? "Approved" : "Pending"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Grid>
          </Grid>
        </>
      )}

      {isFormOpen && (
        <RepulpWasteEntry
          formData={formData}
          setFormData={setFormData}
          setIsFormOpen={setIsFormOpen}
          loadList={loadList}
        />
      )}
    </Grid>
  );
};

export default RepulpWasteListIndex;
